// Cart drawer reservation countdown.
// Settings: Online Store > Customize > Theme settings > Cart > Cart drawer countdown.
//
// The element lives inside .drawer__inner, which the theme replaces wholesale on
// every cart update (add to cart, quantity change, line removal). A custom
// element is used rather than a script bound in place, because scripts inside
// swapped-in markup never run again: this re-initialises itself on connect and
// picks the running clock back up from sessionStorage, so the timer does not
// jump back to full whenever the drawer re-renders or the page reloads.
if (!customElements.get('volan-cart-countdown')) {
  customElements.define(
    'volan-cart-countdown',
    class VolanCartCountdown extends HTMLElement {
      // One clock per browser session, shared by every page that renders the
      // drawer, so browsing the store does not restart the countdown.
      get storageKey() {
        return 'volan-cart-reservation';
      }

      connectedCallback() {
        // Re-connecting (drawer re-render, editor re-init) must not leave the
        // previous interval ticking against the same element.
        window.clearInterval(this.interval);

        const duration = Number.parseInt(this.dataset.seconds, 10);
        const items = Number.parseInt(this.dataset.items, 10);
        if (!Number.isFinite(duration) || duration <= 0) return;

        // Empty cart: there is nothing to reserve, so drop the stored clock and
        // let the next cart start a fresh one.
        if (!Number.isFinite(items) || items <= 0) {
          this.forget();
          return;
        }

        this.time = this.querySelector('[data-countdown-time]');
        if (!this.time) return;

        const deadline = this.reserve(duration * 1000, items);
        this.render(deadline);
        this.interval = window.setInterval(() => this.render(deadline), 1000);
      }

      disconnectedCallback() {
        window.clearInterval(this.interval);
      }

      // The deadline to count down to: the stored one while the cart is
      // unchanged, a fresh window whenever the cart moves (item added, line
      // removed, quantity edited).
      reserve(duration, items) {
        const stored = this.stored();
        if (stored && stored.items === items) return stored.deadline;

        const deadline = Date.now() + duration;
        this.store({ items, deadline });
        return deadline;
      }

      render(deadline) {
        const remaining = deadline - Date.now();
        if (remaining <= 0) {
          window.clearInterval(this.interval);
          this.expire();
          return;
        }
        this.time.textContent = this.format(remaining);
      }

      // m:ss - the setting is capped at 60 minutes, so there is no hour part,
      // and seconds keep two digits so the label does not jitter.
      format(remaining) {
        const total = Math.ceil(remaining / 1000);
        const minutes = Math.floor(total / 60);
        const seconds = total % 60;
        return `${minutes}:${String(seconds).padStart(2, '0')}`;
      }

      expire() {
        // No expired message: the bar simply hides at zero. It stays hidden
        // until the cart changes (which starts a fresh window) or empties.
        this.hidden = true;
      }

      stored() {
        try {
          return JSON.parse(window.sessionStorage.getItem(this.storageKey));
        } catch (error) {
          return null;
        }
      }

      store(state) {
        try {
          window.sessionStorage.setItem(this.storageKey, JSON.stringify(state));
        } catch (error) {
          // Storage blocked (private mode, cookies off): the countdown still
          // runs, it just restarts on the next re-render instead of resuming.
        }
      }

      forget() {
        try {
          window.sessionStorage.removeItem(this.storageKey);
        } catch (error) {
          // Nothing was stored to begin with.
        }
      }
    }
  );
}
